import { useAuth } from '../../auth/AuthContext'
import { acknowledgeSanction, getCoachSanctions } from '../../lib/api'
import { useAsync } from '../../lib/useAsync'
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Loading,
  Notice,
  PageHeader,
  Section,
} from '../../components/ui'
import { formatDay, formatMoney } from '../../lib/format'
import { useAction, useLiveReload } from './coachHooks'
import type { Sanction, SanctionType } from '../../types/db'

const SANCTION_LABEL: Record<SanctionType, string> = {
  warning_1: 'First warning',
  warning_2: 'Second warning',
  penalty_deduction: 'Salary deduction',
  other: 'Note on file',
}

/**
 * My record.
 *
 * Design principle from the brief: fairness over policing. Every entry shows
 * the reason and the contract clause it rests on, revoked entries are shown as
 * revoked rather than deleted, and the coach can acknowledge that they read it.
 */
export function CoachRecord() {
  const { profile } = useAuth()
  const coachId = profile?.id ?? ''
  const { data, error, loading, reload } = useAsync(() => getCoachSanctions(coachId), [coachId])
  useLiveReload(reload)
  const action = useAction(reload)

  if (loading && !data) return <Loading label="Loading your record" />
  if (error && !data) return <ErrorState message={error} onRetry={reload} />

  const rows = data ?? []
  const active = rows.filter((r) => !r.is_revoked)
  const revoked = rows.filter((r) => r.is_revoked)

  function acknowledge(id: string) {
    void action.run(() => acknowledgeSanction(id))
  }

  return (
    <>
      <PageHeader
        title="My record"
        subtitle="Warnings and notes on your file, with the reason behind each one."
      />

      {action.error ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="warning">{action.error}</Notice>
        </div>
      ) : null}

      {profile && !profile.contract_signed ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="warning" title="Your contract is not signed yet">
            Deductions only apply under a signed contract. Ask Michel for your copy so your record
            is on the same footing as everyone else's.
          </Notice>
        </div>
      ) : null}

      {active.length === 0 ? (
        <EmptyState
          title="Your record is clear"
          body="Nothing on file. Warnings only ever appear here with the reason and the contract clause behind them."
        />
      ) : (
        <Section title="On your record" count={active.length}>
          <div className="stack">
            {active.map((row) => (
              <SanctionCard
                key={row.id}
                row={row}
                busy={action.busy}
                onAcknowledge={() => acknowledge(row.id)}
              />
            ))}
          </div>
        </Section>
      )}

      {revoked.length > 0 ? (
        <Section title="Revoked" count={revoked.length}>
          <div className="stack">
            {revoked.map((row) => (
              <SanctionCard
                key={row.id}
                row={row}
                busy={action.busy}
                onAcknowledge={() => undefined}
              />
            ))}
          </div>
        </Section>
      ) : null}

      <Section title="How this works">
        <Card tone="muted">
          <dl className="deflist">
            <div>
              <dt>Cancelling within 12 hours, first time</dt>
              <dd>First warning</dd>
            </div>
            <div>
              <dt>Second time</dt>
              <dd>Second warning</dd>
            </div>
            <div>
              <dt>Third time</dt>
              <dd>AED 50 deduction</dd>
            </div>
          </dl>
          <p className="faint" style={{ marginTop: 'var(--sp-3)' }}>
            Nothing is applied automatically. Michel issues each one by hand and Pascal can revoke
            it if the reason justifies it. Contract clause 4.3.
          </p>
        </Card>
      </Section>
    </>
  )
}

function SanctionCard({
  row,
  busy,
  onAcknowledge,
}: {
  row: Sanction
  busy: boolean
  onAcknowledge: () => void
}) {
  return (
    <Card tone={row.is_revoked ? 'muted' : 'default'}>
      <div className="row row--between">
        <div style={{ minWidth: 0 }}>
          <p className="card__title">{SANCTION_LABEL[row.type]}</p>
          <p className="small muted" style={{ marginTop: 2 }}>
            {formatDay(row.issued_at)}
            {row.contract_clause ? ` · Contract clause ${row.contract_clause}` : ''}
          </p>
        </div>
        {row.is_revoked ? (
          <Badge tone="positive">Revoked</Badge>
        ) : row.acknowledged_at ? (
          <Badge tone="neutral">Read</Badge>
        ) : (
          <Badge tone="warning">Please read</Badge>
        )}
      </div>

      <p className="small" style={{ marginTop: 'var(--sp-3)' }}>
        {row.reason}
      </p>

      {row.amount_aed > 0 ? (
        <p className="small muted" style={{ marginTop: 'var(--sp-2)' }}>
          Amount: {formatMoney(row.amount_aed)}
        </p>
      ) : null}

      {row.is_revoked && row.revoke_reason ? (
        <p className="small muted" style={{ marginTop: 'var(--sp-2)' }}>
          Revoked: {row.revoke_reason}
        </p>
      ) : null}

      {!row.is_revoked ? (
        <div className="card__foot">
          {row.acknowledged_at ? (
            <span className="faint">You marked this as read {formatDay(row.acknowledged_at)}</span>
          ) : (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={busy}
              onClick={onAcknowledge}
            >
              I have read this
            </button>
          )}
        </div>
      ) : null}
    </Card>
  )
}
